/**
 * Générateur — Automatisme : « n fois plus, n fois moins »
 * Attendu ID : 1038 | Niveau : 6ème | Domaine : NP
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève associe de manière
 * automatique les expressions du type "4 fois plus grand, 4 fois plus
 * petit, 5 fois plus, 5 fois moins" à une multiplication ou à une
 * division. »
 * Labels (cartes exerciseur) :
 *   - n fois plus, n fois moins
 *
 * ── Plan de cartes (2026-08-22, cadrage prof) ──────────────────────────────
 * AUTOMATISME LEXICAL : la tournure → l'opération. Quatre formes (« plus
 * grand / plus petit / plus / moins »), et LE PIÈGE CENTRAL prescrit par le
 * programme : « 4 FOIS plus » multiplie, « 4 DE plus » additionne — c'est
 * le mot « fois » qui signale le multiplicatif (~25 % des QCM opération).
 * BORNES : le FACTEUR EST LIBRE quand on ne demande que l'opération
 * (« 47 fois moins » = une division, quel que soit n) ; quand un RÉSULTAT
 * est attendu, bornes du calcul mental CM2 : multiplier par n < 10,
 * diviser par 2, 4, 8 ou un diviseur repérable dans les tables.
 * ~10 % des calculs sur un décimal léger (3 fois plus grand que 1,5).
 * La division s'écrit EN TOUTES LETTRES (jamais le symbole ÷).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux,
} from '../../../utils';

const ATTENDU_ID = 1038;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'n fois plus, n fois moins';

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1038 : ${message}`);
};

const fmtFr = (x) => (Number.isInteger(x) ? String(x) : String(x).replace('.', ','));

/** Les quatre tournures multiplicatives (op : mult | div). */
const TOURNURES = [
    { id: 'plus_grand', txt: (n) => `${n} fois plus grand`, op: 'mult' },
    { id: 'plus_petit', txt: (n) => `${n} fois plus petit`, op: 'div' },
    { id: 'plus', txt: (n) => `${n} fois plus`, op: 'mult' },
    { id: 'moins', txt: (n) => `${n} fois moins`, op: 'div' },
];
/** Le piège central : « de » au lieu de « fois ». */
const PIEGES_DE = [
    { id: 'de_plus', txt: (n) => `${n} de plus`, op: 'add' },
    { id: 'de_moins', txt: (n) => `${n} de moins`, op: 'sous' },
];

const OPERATIONS = {
    mult: (n) => `une multiplication par ${n}`,
    div: (n) => `une division par ${n}`,
    add: (n) => `une addition de ${n}`,
    sous: (n) => `une soustraction de ${n}`,
};

const finirQcm = (bonne, dis, texte, sousType) => {
    const payload = makeQcm(
        [bonne, ...dis.map((c) => c.valeur)],
        bonne,
        texte,
        opts(L1),
        false,
    );
    payload.contenu = { ...payload.contenu, sousType };
    payload._meta.params = { sousType, distracteurs: dis };
    return payload;
};

// ─── Format 1 : QCM opération (le cœur — facteur LIBRE) ───────────────────

const genOperationQcm = () => {
    const piege = Math.random() < 0.25;
    const t = piege ? randomChoice(PIEGES_DE) : randomChoice(TOURNURES);
    // le facteur est libre : jusqu'à 99, pour montrer que rien ne se calcule
    const n = Math.random() < 0.7 ? randInt(2, 12) : randInt(13, 99);
    const bonne = OPERATIONS[t.op](n);
    const dis = shuffle(Object.keys(OPERATIONS).filter((op) => op !== t.op))
        .map((op) => ({
            valeur: OPERATIONS[op](n),
            code: (op === 'mult' && t.op !== 'mult') || (op === 'add' && t.op === 'mult')
                ? 'automatisation_du_mot' : 'autre_operation',
        }));
    return finirQcm(bonne, dis,
        `<p><b>« ${t.txt(n)} », ça correspond à...</b></p>`,
        piege ? 'piege_de' : 'operation');
};

// ─── Formats 2 et 3 : le calcul (bornes CM2) ──────────────────────────────

/** Tirage d'un calcul : mult n < 10 ; div par 2, 3, 4, 5 ou 8 avec une base
 *  dans les tables. Décimal léger ~10 % (côté mult seulement). */
const tirerCalcul = () => {
    const t = randomChoice(TOURNURES);
    let n;
    let b;
    if (t.op === 'mult') {
        // TOUT DE TÊTE (arbitrage prof 2026-08-22) : produits dans les
        // tables (n, b ≤ 10) ; décimaux réservés au double et au triple
        // (les seuls objectifs CM2 sur décimal)
        if (Math.random() < 0.1) {
            n = randInt(2, 3);
            b = randomChoice([1.5, 2.5, 3.5]);
        } else {
            n = randInt(2, 9);
            b = randInt(2, 10);
        }
    } else {
        n = randomChoice([2, 3, 4, 5, 8]);
        b = n * randInt(2, 10);              // b dans la table de n (≤ 80)
    }
    const res = t.op === 'mult' ? n * b : b / n;
    garde(Number.isInteger(res * 2), `résultat non demi-entier : ${t.id} n=${n} b=${b}`);
    const question = (t.id === 'plus_grand' || t.id === 'plus_petit')
        ? `Quel nombre est ${t.txt(n)} que ${fmtFr(b)} ?`
        : `${t.txt(n)} que ${fmtFr(b)}, c'est...`;
    return { t, n, b, res, question };
};

const distracteursCalcul = (t, n, b, res) => {
    const candidats = [];
    if (t.op === 'mult') {
        // L'ERREUR CIBLE du programme : additionner au lieu de multiplier
        candidats.push({ valeur: b + n, code: 'addition' });
        candidats.push({ valeur: b / n, code: 'division' });
    } else {
        candidats.push({ valeur: b - n, code: 'soustraction' });
        candidats.push({ valeur: b * n, code: 'multiplication' });
    }
    candidats.push({ valeur: n, code: 'recopie_facteur' });
    candidats.push({ valeur: b, code: 'recopie_base' });
    [res + 1, res - 1, res + n, res - n].forEach((v) => candidats.push({ valeur: v, code: 'vivier' }));
    const vus = new Set([fmtFr(res)]);
    return candidats
        .filter((c) => c.valeur > 0 && Number.isInteger(c.valeur * 2))
        .map((c) => ({ ...c, valeur: fmtFr(c.valeur) }))
        .filter((c) => {
            if (vus.has(c.valeur)) return false;
            vus.add(c.valeur);
            return true;
        })
        .slice(0, 3);
};

const genCalculQcm = () => {
    const { t, n, b, res, question } = tirerCalcul();
    return finirQcm(fmtFr(res), distracteursCalcul(t, n, b, res),
        `<p><b>${question}</b></p>`, `calcul_${t.id}`);
};

const genCalculSaisie = () => {
    const { t, res, question } = tirerCalcul();
    const payload = makeSaisie(fmtFr(res), `<p><b>${question}</b></p>`, opts(L1));
    payload._meta.params = { sousType: `saisie_${t.id}` };
    return payload;
};

// ─── Format 4 : V/F (numérique + lexical, vérité recalculée) ──────────────

/** L'affirmation fausse plausible pour chaque tournure : l'opération que
 *  l'élève « automatise » à tort (plus → addition, moins → division...). */
const CLAIM_FAUX = { mult: 'add', div: 'sous', add: 'mult', sous: 'div' };

const genVraiFaux = () => {
    for (let essai = 0; essai < 60; essai++) {
        const lignes = [];
        const verites = [];
        const vues = new Set();
        // 2 lignes NUMÉRIQUES (vérité recalculée depuis l'affiché)
        for (let i = 0; i < 2; i++) {
            const { t, n, b, res } = tirerCalcul();
            let estVraie = Math.random() < 0.5;
            let valeur = estVraie ? res : (t.op === 'mult' ? b + n : b - n);
            if (valeur <= 0 || !Number.isInteger(valeur * 2) || Math.abs(valeur - res) < 1e-9) {
                estVraie = true;
                valeur = res;
            }
            const ligne = (t.id === 'plus_grand' || t.id === 'plus_petit')
                ? `${fmtFr(valeur)} est ${t.txt(n)} que ${fmtFr(b)}`
                : `${t.txt(n)} que ${fmtFr(b)}, c'est ${fmtFr(valeur)}`;
            if (vues.has(ligne)) continue;
            vues.add(ligne);
            lignes.push(ligne);
            verites.push(Math.abs(valeur - res) < 1e-9);
        }
        // 2 lignes LEXICALES (le piège fois/de a sa place)
        for (let i = 0; i < 2; i++) {
            const t = randomChoice([...TOURNURES, ...PIEGES_DE]);
            const n = randInt(2, 9);
            const txts = {
                mult: `multiplier par ${n}`, div: `diviser par ${n}`,
                add: `ajouter ${n}`, sous: `soustraire ${n}`,
            };
            const estVraie = Math.random() < 0.5;
            const claim = estVraie ? txts[t.op] : txts[CLAIM_FAUX[t.op]];
            const ligne = `« ${t.txt(n)} » veut dire ${claim}`;
            if (vues.has(ligne)) continue;
            vues.add(ligne);
            lignes.push(ligne);
            verites.push(estVraie);
        }
        if (lignes.length !== 4) continue;
        const nbVraies = verites.filter(Boolean).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        ['24 est 4 fois plus grand que 6', '« 3 de plus » veut dire multiplier par 3',
            '5 fois moins que 30, c\'est 6', '« 2 fois plus » veut dire multiplier par 2'],
        [0, 1, 0, 0],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'AUTOMATISME LEXICAL : la tournure → l\'opération ; le mot FOIS signale le multiplicatif',
        'facteur LIBRE (jusqu\'à 99) quand on ne demande que l\'opération ; bornes CM2 quand un résultat est attendu (× par n < 10 ; division par 2, 3, 4, 5, 8 dans les tables)',
        'le piège central « n DE plus » (addition !) : ~25 % des QCM opération + présent en V/F',
        'la division en toutes lettres, jamais le symbole ÷ ; ~10 % de calculs sur décimal léger',
    ],
    fois_operation_qcm: {
        support: 'la tournure entre guillemets, les 4 opérations en toutes lettres en choix',
        fixes: { automatisation_du_mot: 'l\'opération qu\'un élève associe au mot sans lire « fois »/« de »' },
    },
    fois_calcul_qcm: {
        fixes: {
            addition_soustraction: 'b ± n — L\'ERREUR CIBLE signalée par le programme',
            operation_inverse: 'la division quand il fallait multiplier (et réciproquement)',
            recopie: 'le facteur ou la base recopiés',
        },
        vivier: '±1 / ±n autour du résultat, en complément seulement',
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    fois_operation_qcm: genOperationQcm,
    fois_calcul_qcm: genCalculQcm,
    fois_calcul_saisie: genCalculSaisie,
    fois_vf: genVraiFaux,
};

const FORMAT_LABELS = {
    fois_operation_qcm: 'Quelle opération ?',
    fois_calcul_qcm: 'QCM calcul',
    fois_calcul_saisie: 'Saisie',
    fois_vf: 'Vrai/Faux',
};

export const CATEGORIES = [
    {
        id: 'foisplus',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
